import {
  Button,
  Card,
  Divider,
  Group,
  Icon,
  IconButton,
  Input,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
  Tile,
  Upload,
} from "@assetwatch/ui";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  MotionAmplificationPreset,
  MotionAmplificationRawVideo,
} from "../../clients/motion-amplification";
import { useVideoFrameRate } from "../../hooks/useVideoFrameRate";
import type { MotionAmplificationSubmission } from "../../mutations/useUploadMotionAmplificationVideoMutation";
import { MotionAmplificationParameterForm } from "./MotionAmplificationParameterForm";
import type { MotionAmplificationFormParameters } from "./motionAmplificationParameters";
import {
  createDefaultParameters,
  DEFAULT_PRESET,
  isMotionAmplificationParametersValid,
  isPresetCompatibleWithFps,
  MAX_INPUT_BYTES,
  MAX_VIDEO_DURATION_SECONDS,
  nyquistFrequencyForFps,
  PRESET_DEFAULTS,
} from "./motionAmplificationParameters";

const FPS_PRESET_TARGETS: Array<{
  fps: number;
  preset: MotionAmplificationPreset;
}> = [
  { fps: 240, preset: "high-frequency-240fps" },
  { fps: 120, preset: "high-frequency-120fps" },
  { fps: 60, preset: "standard-60fps" },
];

function presetForFrameRate(fps: number) {
  if (fps < 60 * 0.995) return "high-stability-review" as const;
  return FPS_PRESET_TARGETS.find(
    (target) => Math.abs(fps - target.fps) <= Math.max(1, target.fps * 0.01),
  )?.preset;
}

function formatFileSize(bytes: number) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatUploadDate(value: string | undefined, fallback: string) {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export function MotionAmplificationUploadModal({
  open,
  onOpenChange,
  onSubmit,
  rawVideos,
  isLoadingRawVideos,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (submission: MotionAmplificationSubmission) => Promise<void>;
  rawVideos: MotionAmplificationRawVideo[];
  isLoadingRawVideos: boolean;
}) {
  const { t } = useTranslation();
  const [source, setSource] = useState<"upload" | "existing" | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [existingVideo, setExistingVideo] =
    useState<MotionAmplificationRawVideo | null>(null);
  const [fileName, setFileName] = useState("");
  const [description, setDescription] = useState("");
  const [preset, setPreset] =
    useState<MotionAmplificationPreset>(DEFAULT_PRESET);
  const [parameters, setParameters] =
    useState<MotionAmplificationFormParameters>(createDefaultParameters());
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    fps: localFps,
    durationSeconds: localDurationSeconds,
    isDetecting,
    error: frameRateError,
  } = useVideoFrameRate(file);
  const fps = file ? localFps : (existingVideo?.fps ?? null);
  const durationSeconds = file
    ? localDurationSeconds
    : (existingVideo?.durationSeconds ?? null);
  const roundedFps = fps === null ? null : Math.round(fps);
  const isPresetValid = isPresetCompatibleWithFps(preset, fps);
  const areParametersValid = isMotionAmplificationParametersValid(
    parameters,
    fps,
  );

  useEffect(() => {
    if (fps === null) return;
    const detectedPreset = presetForFrameRate(fps);
    if (!detectedPreset) return;
    setPreset((current) =>
      current === detectedPreset ? current : detectedPreset,
    );
    setParameters((current) => ({
      ...current,
      ...PRESET_DEFAULTS[detectedPreset],
    }));
  }, [fps]);

  function reset() {
    setSource(null);
    setFile(null);
    setExistingVideo(null);
    setFileName("");
    setDescription("");
    setPreset(DEFAULT_PRESET);
    setParameters(createDefaultParameters());
    setShowAdvanced(false);
    setError(null);
    setIsSubmitting(false);
  }
  function updateParameter<Key extends keyof MotionAmplificationFormParameters>(
    key: Key,
    value: MotionAmplificationFormParameters[Key],
  ) {
    setParameters((current) => ({ ...current, [key]: value }));
  }
  function handlePresetChange(value: string | null) {
    if (!value || !(value in PRESET_DEFAULTS)) return;
    const next = value as MotionAmplificationPreset;
    setPreset(next);
    setParameters((current) => ({ ...current, ...PRESET_DEFAULTS[next] }));
  }
  function handleFileSelected(next: File | null) {
    setSource(next ? "upload" : null);
    setFile(next);
    setExistingVideo(null);
    setFileName(next?.name.replace(/\.[A-Za-z0-9]+$/, "") ?? "");
    setError(null);
  }
  function handleExistingVideoSelected(rawObjectKey: string | null) {
    const next =
      rawVideos.find((video) => video.rawObjectKey === rawObjectKey) ?? null;
    setSource(next ? "existing" : null);
    setExistingVideo(next);
    setFile(null);
    setFileName(next?.fileName?.replace(/\.[A-Za-z0-9]+$/, "") ?? "");
    setError(null);
  }
  function clearSource() {
    setSource(null);
    setFile(null);
    setExistingVideo(null);
    setFileName("");
    setError(null);
  }
  async function submit() {
    if (!source || (!file && !existingVideo) || !fileName.trim()) return;
    if (fps === null) {
      setError(t("motionAmplification.waitForFps"));
      return;
    }
    if (
      durationSeconds !== null &&
      durationSeconds > MAX_VIDEO_DURATION_SECONDS
    ) {
      setError(
        t("motionAmplification.durationError", {
          limit: MAX_VIDEO_DURATION_SECONDS,
        }),
      );
      return;
    }
    if (file && file.size > MAX_INPUT_BYTES) {
      setError(
        t("motionAmplification.fileSizeError", {
          limit: MAX_INPUT_BYTES / (1024 * 1024),
        }),
      );
      return;
    }
    if (!isPresetCompatibleWithFps(preset, fps)) {
      setError(t("motionAmplification.presetFpsError"));
      return;
    }
    if (!isMotionAmplificationParametersValid(parameters, fps)) {
      if (
        fps !== null &&
        parameters.frequencyHighHz !== null &&
        parameters.frequencyHighHz > nyquistFrequencyForFps(fps)
      ) {
        setError(
          t("motionAmplification.frequencyError", {
            frequency: nyquistFrequencyForFps(fps).toFixed(0),
          }),
        );
        return;
      }
      setError(t("motionAmplification.numericFieldsError"));
      return;
    }
    try {
      setError(null);
      setIsSubmitting(true);
      const details = {
        name: fileName,
        description,
        preset,
        parameters,
        fps: roundedFps,
      };
      if (file) {
        await onSubmit({ ...details, file, existingVideo: null });
      } else if (existingVideo) {
        await onSubmit({ ...details, file: null, existingVideo });
      } else {
        return;
      }
      reset();
      onOpenChange(false);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : t("motionAmplification.uploadError"),
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (isSubmitting) return;
        if (!nextOpen) reset();
        onOpenChange(nextOpen);
      }}
      title={t("motionAmplification.uploadVideo")}
      size="lg"
      footer={
        <Group justify="end">
          <Button
            variant="ghost"
            onClick={() => {
              reset();
              onOpenChange(false);
            }}
            disabled={isSubmitting}
          >
            {t("motionAmplification.cancel")}
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={
              isSubmitting ||
              !source ||
              (!file && !existingVideo) ||
              !fileName.trim() ||
              (source === "upload" && isDetecting) ||
              (source !== null && fps === null) ||
              (durationSeconds !== null &&
                durationSeconds > MAX_VIDEO_DURATION_SECONDS) ||
              (file !== null && file.size > MAX_INPUT_BYTES) ||
              !isPresetValid ||
              !areParametersValid
            }
            loading={isSubmitting}
            loadingText={t("motionAmplification.uploading")}
          >
            {t("motionAmplification.uploadAndProcess")}
          </Button>
        </Group>
      }
    >
      <Stack gap={20}>
        {source === null ? (
          <>
            <Upload
              variant="file"
              accept="video/*"
              acceptedFormatsLabel={t("motionAmplification.acceptedFormats")}
              maxSizeLabel="500 MB"
              buttonLabel={t("motionAmplification.selectVideo")}
              guidanceLabel={t("motionAmplification.uploadGuidance")}
              onFilesSelected={(files) => handleFileSelected(files[0] ?? null)}
              data-testid="motion-amplification-upload"
              style={{ margin: "auto", width: "100%" }}
            />
            <Divider centeredText={t("motionAmplification.or")} />
            <Select
              label={t("motionAmplification.selectExistingVideo")}
              placeholder={
                isLoadingRawVideos
                  ? t("motionAmplification.loadingExistingVideos")
                  : t("motionAmplification.selectExistingVideo")
              }
              options={rawVideos.map((video) => ({
                value: video.rawObjectKey,
                label: `${video.fileName ?? ""} · ${formatUploadDate(video.dateCreated ?? undefined, t("motionAmplification.noUploadTime"))}`,
              }))}
              value={null}
              onChange={handleExistingVideoSelected}
              disabled={isLoadingRawVideos || rawVideos.length === 0}
            />
          </>
        ) : null}
        {source === "upload" && file ? (
          <Card padding="compact">
            <div className="flex w-full items-center gap-(--spacing-12)">
              <Tile tone="muted" padding="compact" radius="sm">
                <Icon name="document" size="md" />
              </Tile>
              <div className="min-w-0 flex-1">
                <Text truncate title={file.name}>
                  {file.name}
                </Text>
                <Text tone="subtle">{formatFileSize(file.size)}</Text>
                <Text tone="subtle">
                  {fps === null
                    ? isDetecting
                      ? t("motionAmplification.detectingFps")
                      : t("motionAmplification.fpsUnavailable")
                    : `${roundedFps} FPS`}
                </Text>
                {frameRateError && (
                  <Text tone="negative">{frameRateError}</Text>
                )}
                {durationSeconds !== null &&
                  durationSeconds > MAX_VIDEO_DURATION_SECONDS && (
                    <Text tone="negative">
                      {t("motionAmplification.videoTooLong", {
                        seconds: durationSeconds.toFixed(2),
                        limit: MAX_VIDEO_DURATION_SECONDS,
                      })}
                    </Text>
                  )}
                {file.size > MAX_INPUT_BYTES && (
                  <Text tone="negative">
                    {t("motionAmplification.fileSizeError", {
                      limit: MAX_INPUT_BYTES / (1024 * 1024),
                    })}
                  </Text>
                )}
              </div>
              <IconButton
                variant="ghost"
                intent="destructive"
                icon="trash-delete-bin"
                aria-label={t("motionAmplification.removeAttachedVideo")}
                onClick={() => handleFileSelected(null)}
                disabled={isSubmitting}
              />
            </div>
          </Card>
        ) : source === "existing" && existingVideo ? (
          <Card padding="compact">
            <div className="flex w-full items-center gap-(--spacing-12)">
              <Tile tone="muted" padding="compact" radius="sm">
                <Icon name="document" size="md" />
              </Tile>
              <div className="min-w-0 flex-1">
                <Text truncate title={existingVideo.fileName ?? undefined}>
                  {existingVideo.fileName}
                </Text>
                <Text tone="subtle">
                  {formatFileSize(existingVideo.fileSize)}
                </Text>
                <Text tone="subtle">
                  {fps === null
                    ? t("motionAmplification.fpsUnavailable")
                    : `${roundedFps} FPS`}
                </Text>
                {frameRateError && (
                  <Text tone="negative">{frameRateError}</Text>
                )}
                {durationSeconds !== null &&
                  durationSeconds > MAX_VIDEO_DURATION_SECONDS && (
                    <Text tone="negative">
                      {t("motionAmplification.videoTooLong", {
                        seconds: durationSeconds.toFixed(2),
                        limit: MAX_VIDEO_DURATION_SECONDS,
                      })}
                    </Text>
                  )}
              </div>
              <IconButton
                variant="ghost"
                intent="destructive"
                icon="trash-delete-bin"
                aria-label={t("motionAmplification.removeSelectedVideo")}
                onClick={clearSource}
                disabled={isSubmitting}
              />
            </div>
          </Card>
        ) : null}
        <Input
          label={t("motionAmplification.fileName")}
          placeholder={t("motionAmplification.fileNamePlaceholder")}
          value={fileName}
          onChange={(event) => setFileName(event.target.value)}
          maxLength={100}
          tooltip={t("motionAmplification.fileNameHint")}
          title={t("motionAmplification.fileNameHint")}
          reserveSubtextSpace={false}
        />
        <Textarea
          label={t("motionAmplification.description")}
          placeholder={t("motionAmplification.descriptionPlaceholder")}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          maxLength={10000}
        />
        <MotionAmplificationParameterForm
          parameters={parameters}
          updateParameter={updateParameter}
          preset={preset}
          onPresetChange={handlePresetChange}
          fps={fps}
          showAdvanced={showAdvanced}
          onToggleAdvanced={() => setShowAdvanced((current) => !current)}
        />
        {error && <Text tone="negative">{error}</Text>}
      </Stack>
    </Modal>
  );
}
